import { useState, useRef, useEffect } from 'react';
import { EditableSection, EditableSectionRef } from '../helpers/EditableSection';
import { ConfirmationModal } from '../helpers/ConfirmationModal';
import { trpc } from '../../lib/trpc';
import { Bucket, VideoFileExtension, ImageFileExtension } from '@mightybyte/business';
import { CustomButton } from '../uiWrappers/button';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { FaRegTrashCan } from 'react-icons/fa6';
import { Progress } from '@/components/ui/progress.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { PiCamera } from 'react-icons/pi';

interface QuizSettingsProps {
  quizId: string;
  quizNumber: number;
}

interface VideoUploadState {
  uploading: boolean;
  progress: number;
  error?: string;
  fileName?: string;
}

export function QuizSettings({ quizId, quizNumber }: QuizSettingsProps) {
  const utils = trpc.useUtils();
  const { data } = trpc.admin.quiz.getById.useQuery({ id: quizId });
  const quiz = data?.quiz;

  // Refs for file inputs
  const fileInputRefs = useRef<Record<Bucket, HTMLInputElement | null>>({
    [Bucket.One]: null,
    [Bucket.Two]: null,
  });

  // Ref for closing the modal
  const editableSectionRef = useRef<EditableSectionRef>(null);

  // Track upload states and temporary filenames (before saving)
  const [uploadStates, setUploadStates] = useState<Record<Bucket, VideoUploadState>>({
    [Bucket.One]: { uploading: false, progress: 0 },
    [Bucket.Two]: { uploading: false, progress: 0 },
  });

  // Temporary state for title before saving
  const [tempTitle, setTempTitle] = useState<string>('');

  // Sync tempTitle when quiz data loads
  useEffect(() => {
    if (quiz) {
      setTempTitle(quiz.title ?? '');
    }
  }, [quiz?.title]);

  // Temporary state for filenames before saving
  const [tempVideos, setTempVideos] = useState<Partial<Record<Bucket, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Track which videos are marked for deletion (before saving)
  const [videosToDelete, setVideosToDelete] = useState<Set<Bucket>>(new Set());

  // Title image state
  const titleImageInputRef = useRef<HTMLInputElement>(null);
  const [tempTitleImageFileName, setTempTitleImageFileName] = useState<string>('');
  const [tempTitleImagePreview, setTempTitleImagePreview] = useState<string>('');
  const [isUploadingTitleImage, setIsUploadingTitleImage] = useState(false);
  const [titleImageUploadError, setTitleImageUploadError] = useState<string>('');
  const [titleImageMarkedForDeletion, setTitleImageMarkedForDeletion] = useState(false);

  // Confirmation modal state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    bucket: Bucket | null;
  }>({ isOpen: false, bucket: null });

  const updateQuizMutation = trpc.admin.quiz.updateQuiz.useMutation({
    onSuccess: () => {
      utils.admin.quiz.getById.invalidate({ id: quizId });
      // Clear temp state after successful save
      setTempVideos({});
      setVideosToDelete(new Set());
      setSaveError(null);
      // Close the modal
      editableSectionRef.current?.closeModal();
      // Show success toast
      toast.success('Module settings saved successfully');
    },
    onError: (error) => {
      // Keep modal open and show error
      setSaveError(error.message || 'Failed to save module settings');
      toast.error('Failed to save module settings');
    },
  });

  const deleteVideoMutation = trpc.admin.quiz.deleteVideo.useMutation();

  const getUploadUrlMutation = trpc.admin.quiz.getVideoUploadUrl.useMutation();
  const getTitleImageUploadUrlMutation = trpc.admin.quiz.getQuizTitleImageUploadUrl.useMutation();
  const startConvertMutation = trpc.admin.video.convertToiOS.useMutation();

  const handleTitleImageUpload = async (file: File) => {
    try {
      setIsUploadingTitleImage(true);
      setTitleImageUploadError('');

      let fileExtension: ImageFileExtension | null = null;
      if (file.type === 'image/png') fileExtension = ImageFileExtension.PNG;
      else if (file.type === 'image/jpeg' || file.type === 'image/jpg') fileExtension = ImageFileExtension.JPG;
      else if (file.type === 'image/webp') fileExtension = ImageFileExtension.WEBP;

      if (!fileExtension) {
        throw new Error('Only PNG, JPG, and WEBP images are supported.');
      }

      const uploadData = await getTitleImageUploadUrlMutation.mutateAsync({ fileExtension });

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.addEventListener('load', () => xhr.status === 200 ? resolve() : reject(new Error('Upload failed')));
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open(uploadData.requestMethod, uploadData.signedUrl);
        Object.entries(uploadData.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.send(file);
      });

      setTempTitleImageFileName(uploadData.fileName);
      setTempTitleImagePreview(URL.createObjectURL(file));
      setTitleImageMarkedForDeletion(false);
    } catch (error) {
      setTitleImageUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploadingTitleImage(false);
    }
  };

  const handleVideoUpload = async (bucket: Bucket, file: File) => {
    try {
      setUploadStates(prev => ({
        ...prev,
        [bucket]: { uploading: true, progress: 0, error: undefined },
      }));

      // Get file extension
      const extension = file.name.split('.').pop()?.toLowerCase() as VideoFileExtension;
      if (extension !== 'mov' && extension !== 'mp4') {
        throw new Error('Only MP4 and MOV formats are supported. Please convert your video before uploading.');
      }

      // Get signed upload URL with original filename
      const uploadData = await getUploadUrlMutation.mutateAsync({
        fileExtension: extension,
        originalFileName: file.name,
      });

      // Upload file to GCS
      const xhr = new XMLHttpRequest();

      await new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadStates(prev => ({
              ...prev,
              [bucket]: { ...prev[bucket], progress },
            }));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve(xhr.response);
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

        xhr.open(uploadData.requestMethod, uploadData.signedUrl);

        Object.entries(uploadData.headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });

        xhr.send(file);
      });

      // Start background conversion job
      setUploadStates(prev => ({
        ...prev,
        [bucket]: { uploading: true, progress: 100, error: undefined },
      }));

      const { jobId } = await startConvertMutation.mutateAsync({
        fileName: uploadData.fileName,
        outputFileName: uploadData.fileName.replace(/\.[^/.]+$/, '-converted.mp4'),
        deleteOriginal: true,
      });

      // Poll until the conversion job completes
      const convertedFileName = await new Promise<string>((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const status = await utils.admin.video.convertStatus.fetch({ jobId }, { staleTime: 0 });
            if (status.status === 'completed' && status.fileName) {
              clearInterval(interval);
              resolve(status.fileName);
            } else if (status.status === 'failed') {
              clearInterval(interval);
              reject(new Error(status.error || 'Conversion failed'));
            }
          } catch (err) {
            clearInterval(interval);
            reject(err);
          }
        }, 2000);
      });

      // Store compressed filename temporarily (don't save to DB yet)
      setTempVideos(prev => ({
        ...prev,
        [bucket]: convertedFileName,
      }));

      setUploadStates(prev => ({
        ...prev,
        [bucket]: { uploading: false, progress: 100, fileName: convertedFileName },
      }));
    } catch (error) {
      console.error('Video upload error:', error);
      setUploadStates(prev => ({
        ...prev,
        [bucket]: {
          uploading: false,
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed',
        },
      }));
    }
  };

  const handleFileSelect = (bucket: Bucket, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void handleVideoUpload(bucket, file);
    }
    // Reset input value so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setSaveError(null);

      // First, delete videos marked for deletion from GCS
      for (const bucket of videosToDelete) {
        const videoInfo = quiz?.videos?.[bucket];
        if (videoInfo) {
          try {
            await deleteVideoMutation.mutateAsync({
              id: quizId,
              bucket,
            });
          } catch (error) {
            console.error(`Failed to delete video from bucket ${bucket}:`, error);
            throw error; // Re-throw to trigger onError
          }
        }
      }

      // Then update with new videos and audios
      // Extract just the filenames from current videos (API returns {fileName, url} but expects just filename strings)
      const currentVideoFilenames: Partial<Record<Bucket, string>> = {};
      if (quiz?.videos) {
        Object.entries(quiz.videos).forEach(([bucket, videoInfo]) => {
          currentVideoFilenames[bucket as Bucket] = videoInfo.fileName;
        });
      }

      const updatedVideos = {
        ...currentVideoFilenames,
        ...tempVideos,
      };

      // Remove videos marked for deletion
      videosToDelete.forEach(bucket => {
        delete updatedVideos[bucket];
      });

      await updateQuizMutation.mutateAsync({
        id: quizId,
        title: tempTitle.trim() || undefined,
        titleImage: titleImageMarkedForDeletion ? '' : (tempTitleImageFileName || undefined),
        videos: updatedVideos,
      });
    } catch (error) {
      // Error is handled by onError callback
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    // Clear temporary uploads, deletions, and errors
    setTempVideos({});
    setVideosToDelete(new Set());
    setSaveError(null);
    setTempTitle(quiz?.title ?? '');
    setTempTitleImageFileName('');
    setTempTitleImagePreview('');
    setTitleImageMarkedForDeletion(false);
    setTitleImageUploadError('');
    setUploadStates({
      [Bucket.One]: { uploading: false, progress: 0 },
      [Bucket.Two]: { uploading: false, progress: 0 },
    });
    // Close the modal
    editableSectionRef.current?.closeModal();
  };

  const handleDeleteVideo = (bucket: Bucket) => {
    setDeleteConfirmation({ isOpen: true, bucket });
  };

  const confirmDeleteVideo = () => {
    if (!deleteConfirmation.bucket) return;

    // Mark video for deletion (staged, not immediate)
    setVideosToDelete(prev => new Set(prev).add(deleteConfirmation.bucket!));
    setDeleteConfirmation({ isOpen: false, bucket: null });
  };

  const renderBucketSettings = (bucket: Bucket, label: string) => {
    const currentVideo = quiz?.videos?.[bucket];
    const tempFileName = tempVideos[bucket];
    const uploadState = uploadStates[bucket];
    const isMarkedForDeletion = videosToDelete.has(bucket);

    const triggerFileInput = () => {
      fileInputRefs.current[bucket]?.click();
    };

    return (
      <div key={bucket} className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-foreground">{label}</h3>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-sm text-blue-900 font-medium">Video Format Support</p>
          <p className="text-sm text-blue-700 mt-1">
            Supported formats: MP4 and MOV. For best compatibility across all devices, use H.264 codec.
          </p>
        </div>

        <input
          ref={(el) => (fileInputRefs.current[bucket] = el)}
          type="file"
          accept="video/mp4,video/quicktime,.mp4,.mov"
          onChange={(e) => handleFileSelect(bucket, e)}
          className="hidden"
          disabled={uploadState.uploading}
        />

        {uploadState.uploading && (
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              {uploadState.progress < 100 ? `Uploading... ${uploadState.progress}%` : 'Compressing video...'}
            </p>
            <Progress value={uploadState.progress} />
          </div>
        )}

        {uploadState.error && (
          <p className="text-sm text-destructive">{uploadState.error}</p>
        )}

        {/* Show temp filename if just uploaded */}
        {tempFileName && !uploadState.uploading && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-sm text-blue-900 font-medium">New upload (not saved yet):</p>
            <p className="text-sm text-blue-700 break-all mt-1">{tempFileName}</p>
          </div>
        )}

        {/* Show marked for deletion state */}
        {isMarkedForDeletion && !tempFileName && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-sm text-red-900 font-medium">Marked for deletion (not saved yet)</p>
          </div>
        )}

        {/* Show current saved video */}
        {currentVideo && !tempFileName && !isMarkedForDeletion && (
          <div className="space-y-2">
            <div className="flex gap-2 items-start">
              <video
                src={currentVideo.url}
                controls
                className="rounded border border-border"
                style={{ maxHeight: '120px', maxWidth: '280px' }}
              />
              <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground font-medium">Current video:</p>
                <p className="text-xs text-muted-foreground">{currentVideo.fileName}</p>
                <div className={'flex gap-2'}>
                  <CustomButton
                      variant="outline"
                      size="sm"
                      disabled={uploadState.uploading}
                      onClick={triggerFileInput}
                  >
                    {uploadState.uploading ? 'Uploading...' : 'Upload Video'}
                  </CustomButton>
                  <CustomButton
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteVideo(bucket)}
                  >
                    Delete
                  </CustomButton>
                </div>

              </div>
            </div>
          </div>
        )}

        {!currentVideo && !tempFileName && !uploadState.uploading && !isMarkedForDeletion && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">No video uploaded</p>
            <CustomButton
              variant="outline"
              size="sm"
              disabled={uploadState.uploading}
              onClick={triggerFileInput}
            >
              {uploadState.uploading ? 'Uploading...' : 'Upload Video'}
            </CustomButton>
          </div>
        )}
      </div>
    );
  };

  const hasChanges = Object.keys(tempVideos).length > 0 || videosToDelete.size > 0 || tempTitle !== (quiz?.title ?? '') || !!tempTitleImageFileName || titleImageMarkedForDeletion;
  const isSaving = updateQuizMutation.isPending;

  const editModalContent = (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        {/* Title field */}
        <div className="space-y-1">
          <Label htmlFor="quizTitleInput">Module Title</Label>
          <Input
            id="quizTitleInput"
            type="text"
            value={tempTitle}
            onChange={(e) => setTempTitle(e.target.value)}
            placeholder="Enter module title"
          />
        </div>
        <div className="border-t border-gray-200 my-4" />

        {/* Title Image */}
        <div className="space-y-2">
          <Label>Module Title Image (optional)</Label>
          <p className="text-xs text-muted-foreground">If not provided, the default module background image is used.</p>

          <input
            ref={titleImageInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleTitleImageUpload(file);
              if (e.target) e.target.value = '';
            }}
          />

          {isUploadingTitleImage && <p className="text-sm text-muted-foreground">Uploading...</p>}
          {titleImageUploadError && <p className="text-sm text-destructive">{titleImageUploadError}</p>}

          {/* Temp preview (just uploaded, not saved yet) */}
          {tempTitleImagePreview && !isUploadingTitleImage && (
            <div className="space-y-2">
              <p className="text-sm text-blue-900 font-medium">New image (not saved yet):</p>
              <img src={tempTitleImagePreview} alt="Title preview" className="rounded border border-border max-h-[120px] object-cover" />
              <CustomButton variant="outline" size="sm" onClick={() => { setTempTitleImageFileName(''); setTempTitleImagePreview(''); }}>
                Remove new image
              </CustomButton>
            </div>
          )}

          {/* Marked for deletion */}
          {titleImageMarkedForDeletion && !tempTitleImagePreview && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-sm text-red-900 font-medium">Title image marked for deletion (not saved yet)</p>
              <CustomButton variant="outline" size="sm" className="mt-2" onClick={() => setTitleImageMarkedForDeletion(false)}>
                Undo
              </CustomButton>
            </div>
          )}

          {/* Current saved image */}
          {quiz?.titleImage && !tempTitleImagePreview && !titleImageMarkedForDeletion && (
            <div className="space-y-2">
              <img src={quiz.titleImage.medium} alt="Current title image" className="rounded border border-border max-h-[120px] object-cover" />
              <div className="flex gap-2">
                <CustomButton variant="outline" size="sm" disabled={isUploadingTitleImage} onClick={() => titleImageInputRef.current?.click()}>
                  Replace Image
                </CustomButton>
                <CustomButton variant="destructive" size="sm" onClick={() => setTitleImageMarkedForDeletion(true)}>
                  Delete
                </CustomButton>
              </div>
            </div>
          )}

          {!quiz?.titleImage && !tempTitleImagePreview && !titleImageMarkedForDeletion && !isUploadingTitleImage && (
            <CustomButton variant="outline" size="sm" onClick={() => titleImageInputRef.current?.click()}>
              <PiCamera className="mr-2 size-4" />
              Upload Image
            </CustomButton>
          )}
        </div>

        <div className={'flex flex-row' }>
          {renderBucketSettings(Bucket.One, 'Bucket One Video')}
          <div className="border-r border-gray-200 mx-4" />
          {renderBucketSettings(Bucket.Two, 'Bucket Two Video')}
        </div>
        {/*<div className="border-t border-gray-200 my-4" />*/}

        {/*<div className="border-t border-gray-200 my-4" />*/}


        {/* Show save error in modal */}
        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-sm text-red-900 font-medium">Error saving module settings:</p>
            <p className="text-sm text-red-700 mt-1">{saveError}</p>
          </div>
        )}
      </div>

      {/* Action buttons pinned at the bottom */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-4 flex-shrink-0">
        <CustomButton
          variant="secondary"
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="default"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </CustomButton>
      </div>
    </div>
  );

  const displayContent = (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-text-secondary mb-1">Title</h3>
        <p className="text-base font-semibold text-foreground">{quiz?.title || '-'}</p>
      </div>
      <div>
        <h3 className="text-sm font-medium text-text-secondary mb-2">Title Image</h3>
        {quiz?.titleImage ? (
          <img src={quiz.titleImage.medium} alt="Module title image" className="rounded border border-border max-h-[120px] object-cover" />
        ) : (
          <p className="text-sm text-muted-foreground">No title image (using default background)</p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-medium text-text-secondary mb-2">Bucket One</h3>
          {quiz?.videos?.[Bucket.One] ? (
            <div className="space-y-2">

              <video
                src={quiz.videos[Bucket.One].url}
                controls
                className="w-full rounded border border-border"
                style={{ maxHeight: '200px', maxWidth:'350px' }}
              />
              <p className="text-xs text-muted-foreground">{quiz.videos[Bucket.One].fileName}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No video</p>
          )}
        </div>
        <div>
          <h3 className="text-sm font-medium text-text-secondary mb-2">Bucket Two</h3>
          {quiz?.videos?.[Bucket.Two] ? (
            <div className="space-y-2">

              <video
                src={quiz.videos[Bucket.Two].url}
                controls
                className="w-full rounded border border-border"
                style={{ maxHeight: '200px', maxWidth:'350px' }}
              />
              <p className="text-xs text-muted-foreground">{quiz.videos[Bucket.Two].fileName}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No video</p>
          )}
        </div>
      </div>
    </div>
  );

  const bucketLabel = deleteConfirmation.bucket === Bucket.One ? 'Bucket One' : 'Bucket Two';

  return (
    <>
      <EditableSection
        ref={editableSectionRef}
        title="Settings"
        onEdit={true}
        editModalTitle={`Edit Module ${quiz?.number} Settings`}
        editModalMaxWidth="2xl"
        editModalContent={editModalContent}
        collapsible={true}
        defaultExpanded={quiz && quiz?.isDraft}
      >
        {displayContent}
      </EditableSection>

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, bucket: null })}
        onConfirm={confirmDeleteVideo}
        title="Mark for Deletion?"
        description="This video will be marked for deletion. Click Save to apply changes."
        confirmText="Mark for Deletion"
        cancelText="Cancel"
        icon={
          <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center">
            <FaRegTrashCan className="w-9 h-9 text-red-500" />
          </div>
        }
        highlightBox={{
          text: `${bucketLabel} Video`,
        }}
      />
    </>
  );
}
